import {
  REEFER_TYPOGRAPHY_FONT_WEIGHT_REGULAR,
  REEFER_TYPOGRAPHY_FONT_WEIGHT_SEMIBOLD,
} from '../../tokens/typography';
import { isFontFile } from '../loadCustomFonts';
import type { ReeferTheme } from '../theme.types';
import { generateReeferThemeComponentsCSSVars } from './components';
import { generateCSSVars } from './utils';

export function generateThemeCSSVars(theme: ReeferTheme) {
  return {
    ...generateReeferThemeComponentsCSSVars(theme.components),
    ...generateCSSVars(theme.borderRadius, 'border-radius'),

    // Colors (includes gradients as a subsection)
    ...generateCSSVars(theme.colors, 'colors'),
    '--colors-inherit': 'inherit',
    '--colors-transparent': 'transparent',

    // Fonts
    '--font-family-default': `${theme.components.Typography.fonts.default.fontFamily}, Source Sans Pro, Helvetica, Arial, sans-serif`,
    '--font-family-branded': `${theme.components.Typography.fonts.branded.fontFamily}, Source Sans Pro, Helvetica, Arial, sans-serif`,
    '--font-weight-regular': REEFER_TYPOGRAPHY_FONT_WEIGHT_REGULAR,
    '--font-weight-semibold': REEFER_TYPOGRAPHY_FONT_WEIGHT_SEMIBOLD,

    // Disabled Opacities
    '--opacity-disabled-regular': 0.6,
    '--opacity-disabled-btn': 0.5,
    '--opacity-disabled-numberfield-input': 0.9,
  };
}

export function generateThemeCSS(
  theme: ReeferTheme,
  id?: string,
  legacy = false
) {
  const cssVars = generateThemeCSSVars(theme);
  const fonts = theme.components.Typography.fonts;

  let themeCss = '';

  if (fonts.default.url) {
    if (isFontFile(fonts.default.url)) {
      themeCss += `
      @font-face {
        font-family: ${fonts.default.fontFamily};
        src: url("${fonts.default.url}");
        font-display: swap;
      }
      \n\n`;
    } else {
      themeCss += `@import url(${fonts.default.url});\n\n`;
    }
  }

  if (fonts.branded.url) {
    if (isFontFile(fonts.branded.url)) {
      themeCss += `
      @font-face {
        font-family: ${fonts.branded.fontFamily};
        src: url("${fonts.branded.url}");
        font-display: swap;
      }\n\n`;
    } else {
      themeCss += `@import url(${fonts.branded.url});\n\n`;
    }
  }

  if (!legacy) {
    themeCss += '@layer reefer {\n';
  }
  // NOTE: When no `id` is provided, the output uses a `:root { ... }` selector.
  // Shadow DOM consumers (framelessEmbedAppContainer, EmbeddedMenuRow) rely on
  // this and do a string replace from `:root {` to `:host {`. If you change the
  // selector format, update those call sites as well.
  themeCss += id ? `#${id} {` : ':root {';

  Object.entries(cssVars).forEach(([key, value]) => {
    themeCss += `\n  ${key}: ${value};`;
  });

  themeCss += '\n}';
  if (!legacy) {
    themeCss += '\n}';
  }

  return themeCss;
}
